/**
 * Grossman-Miller Model Engine
 *
 * Pure functional implementation of the Grossman-Miller market making model.
 * All computations are closed-form solutions from the original 1988 paper.
 *
 * Reference: Grossman, S.J. and Miller, M.H. (1988), "Liquidity and Market Structure,"
 * The Journal of Finance, Vol. 43, No. 3, pp. 617-633.
 */

import {
  ModelParams,
  Shocks,
  EquilibriumResult,
  AgentState,
  SimulationState,
  SimulationOptions,
  TimeStep,
} from './types';
import { validateParams } from './validation';
import { SeededRNG, generateRandomSeed } from './rng';

// ============================================================================
// Core Equilibrium Computations
// ============================================================================

/**
 * Compute equilibrium price at t=1 without fees (PRD FR-5)
 * S_1 = mu - (a * sigma^2 * i) / (n + 1)
 */
function computeS1_NoFees(params: ModelParams): number {
  const { mu, a, sigma, i, n } = params;
  return mu - (a * Math.pow(sigma, 2) * i) / (n + 1);
}

/**
 * Compute equilibrium price at t=1 with fees (PRD FR-22)
 * S_1 = mu - (a * sigma^2 * i) / (n + 1) - (2 * n / (n + 1)) * eta
 */
function computeS1_WithFees(params: ModelParams): number {
  const { mu, a, sigma, i, n, eta } = params;
  const riskComponent = (a * Math.pow(sigma, 2) * i) / (n + 1);
  const feeComponent = (2 * n / (n + 1)) * eta;
  return mu - riskComponent - feeComponent;
}

/**
 * Compute equilibrium holdings at t=1 without fees (PRD FR-6)
 * Each agent holds i / (n + 1) after trading
 */
function computeHoldings_NoFees(params: ModelParams): { q1_MM: number; q1_LT1: number } {
  const { i, n } = params;
  const holdingPerAgent = i / (n + 1);
  return {
    q1_MM: holdingPerAgent,
    q1_LT1: holdingPerAgent,
  };
}

/**
 * Compute equilibrium holdings at t=1 with fees (PRD FR-23)
 */
function computeHoldings_WithFees(params: ModelParams): { q1_MM: number; q1_LT1: number } {
  const { i, n, eta, a, sigma } = params;
  const baseHolding = i / (n + 1);
  const feeAdjustment = (2 / (n + 1)) * (eta / (a * Math.pow(sigma, 2)));

  return {
    q1_MM: baseHolding - feeAdjustment,
    q1_LT1: baseHolding + (n * feeAdjustment),
  };
}

/**
 * Compute liquidity discount decomposition (PRD FR-24)
 */
function computeLiquidityDiscountDecomposition(params: ModelParams): {
  total: number;
  riskComponent: number;
  feeComponent: number;
} {
  const { a, sigma, i, n, eta } = params;
  const riskComponent = (a * Math.pow(sigma, 2) * i) / (n + 1);
  const feeComponent = eta > 0 ? (2 * n / (n + 1)) * eta : 0;
  return {
    total: riskComponent + feeComponent,
    riskComponent,
    feeComponent,
  };
}

/**
 * Compute immediacy measure (PRD FR-10)
 * Immediacy = n / (n + 1)
 */
function computeImmediacy(n: number): number {
  return n / (n + 1);
}

/**
 * Compute price at t=2 (PRD FR-12)
 * S_2 = mu + epsilon_2
 */
function computeS2(mu: number, epsilon2: number): number {
  return mu + epsilon2;
}

/**
 * Compute terminal value at t=3 (PRD FR-16)
 * S_3 = mu + epsilon_2 + epsilon_3
 */
function computeS3(mu: number, epsilon2: number, epsilon3: number): number {
  return mu + epsilon2 + epsilon3;
}

/**
 * Compute price autocorrelation (PRD FR-27, FR-28)
 * q = -t / (1 + t)
 * where t = a^2 * Var_1(S_2) * Var(i) / (1 + n)^2
 */
export function computeAutocorrelation(
  params: ModelParams,
  varianceOfI?: number
): { q: number; t: number } {
  const { a, sigma, n, i } = params;

  // Var_1(S_2) = sigma^2
  const var_S2 = Math.pow(sigma, 2);

  // For deterministic i, we use i^2 * 0.1 as hypothetical variance for demonstration
  // In practice with stochastic i, this would be the actual variance
  const var_i = varianceOfI ?? Math.pow(i, 2) * 0.1;

  const t = Math.pow(a, 2) * var_S2 * var_i / Math.pow(1 + n, 2);
  const q = -t / (1 + t);

  return { q, t };
}

/**
 * Solve for endogenous number of market makers (PRD FR-25, FR-26)
 * Free entry condition: expected profit = participation cost c
 */
export function solveEndogenousN(params: ModelParams): number | null {
  const { c, a, sigma, i } = params;

  if (c <= 0) return null;

  // Expected profit per MM: a * sigma^2 * i^2 / (n+1)^2
  // Entry occurs until: a * sigma^2 * i^2 / (n+1)^2 = c
  // (n+1)^2 = a * sigma^2 * i^2 / c
  // n = sqrt(a * sigma^2 * i^2 / c) - 1

  const var_S2 = Math.pow(sigma, 2);
  const numerator = a * var_S2 * Math.pow(i, 2);

  if (numerator <= c) {
    return 0; // No profitable entry
  }

  const n_equilibrium = Math.sqrt(numerator / c) - 1;
  return Math.max(1, Math.floor(n_equilibrium));
}

/**
 * Compute risk exposure (variance of wealth change) (PRD FR-19)
 */
function computeRiskExposure(holding: number, sigma: number): number {
  return Math.pow(holding, 2) * Math.pow(sigma, 2);
}

// ============================================================================
// Main Computation Functions
// ============================================================================

/**
 * Compute full equilibrium result from parameters
 */
export function computeEquilibrium(
  params: ModelParams,
  shocks: Shocks = { epsilon2: 0, epsilon3: 0 }
): EquilibriumResult {
  const { n, mu, eta, c } = params;
  const includeFees = eta > 0;

  // Compute S1 and holdings based on fee presence
  let S1: number;
  let q1_MM: number;
  let q1_LT1: number;

  if (includeFees) {
    S1 = computeS1_WithFees(params);
    const holdings = computeHoldings_WithFees(params);
    q1_MM = holdings.q1_MM;
    q1_LT1 = holdings.q1_LT1;
  } else {
    S1 = computeS1_NoFees(params);
    const holdings = computeHoldings_NoFees(params);
    q1_MM = holdings.q1_MM;
    q1_LT1 = holdings.q1_LT1;
  }

  // Compute S2 and S3 with actual shocks
  const S2 = computeS2(mu, shocks.epsilon2);
  const S3 = computeS3(mu, shocks.epsilon2, shocks.epsilon3);

  // Compute liquidity discount decomposition
  const discountDecomp = computeLiquidityDiscountDecomposition(params);

  // Compute autocorrelation
  const autoCorr = computeAutocorrelation(params);

  // Solve for endogenous n if participation cost specified
  const endogenousN = c > 0 ? solveEndogenousN(params) ?? undefined : undefined;

  return {
    S1,
    S2,
    S3,
    q1_MM,
    q1_LT1,
    liquidityDiscount: discountDecomp.total,
    riskComponent: discountDecomp.riskComponent,
    feeComponent: discountDecomp.feeComponent,
    immediacy: computeImmediacy(n),
    autocorrelation: autoCorr.q,
    autocorrelationT: autoCorr.t,
    endogenousN,
  };
}

/**
 * Compute agent states for all time steps
 */
export function computeAgentStates(
  params: ModelParams,
  eq: EquilibriumResult,
  _shocks: Shocks
): {
  MM: Record<TimeStep, AgentState>;
  LT1: Record<TimeStep, AgentState>;
  LT2: Record<TimeStep, AgentState>;
} {
  const { i, W0, sigma, eta } = params;
  const { S1, S2 } = eq;
  const { q1_MM, q1_LT1 } = eq;
  const includeFees = eta > 0;

  // ========== TIME 1 ==========

  // MM at t=1: buys q1_MM units
  const tradeMM_t1 = q1_MM;
  const feesPaidMM_t1 = includeFees ? eta * Math.abs(tradeMM_t1) : 0;
  const cashMM_t1 = W0 - S1 * tradeMM_t1 - feesPaidMM_t1;

  // LT1 at t=1: sells from i down to q1_LT1
  const tradeLT1_t1 = q1_LT1 - i; // negative (selling)
  const feesPaidLT1_t1 = includeFees ? eta * Math.abs(tradeLT1_t1) : 0;
  const cashLT1_t1 = W0 - S1 * tradeLT1_t1 - feesPaidLT1_t1; // -S1 * negative = receives cash

  // LT2 at t=1: not participating yet
  const cashLT2_t1 = W0;

  const MM_t1: AgentState = {
    inventory: q1_MM,
    cash: cashMM_t1,
    cumulativePnL: cashMM_t1 - W0,
    riskExposure: computeRiskExposure(q1_MM, sigma),
    tradeThisPeriod: tradeMM_t1,
  };

  const LT1_t1: AgentState = {
    inventory: q1_LT1,
    cash: cashLT1_t1,
    cumulativePnL: cashLT1_t1 - W0,
    riskExposure: computeRiskExposure(q1_LT1, sigma),
    tradeThisPeriod: tradeLT1_t1,
  };

  const LT2_t1: AgentState = {
    inventory: 0,
    cash: cashLT2_t1,
    cumulativePnL: 0,
    riskExposure: 0,
    tradeThisPeriod: 0,
  };

  // ========== TIME 2 ==========

  // All agents unwind to zero inventory at t=2
  // MM sells q1_MM at S2
  const tradeMM_t2 = -q1_MM;
  const feesPaidMM_t2 = includeFees ? eta * Math.abs(tradeMM_t2) : 0;
  const cashMM_t2 = cashMM_t1 + S2 * q1_MM - feesPaidMM_t2;

  // LT1 sells q1_LT1 at S2
  const tradeLT1_t2 = -q1_LT1;
  const feesPaidLT1_t2 = includeFees ? eta * Math.abs(tradeLT1_t2) : 0;
  const cashLT1_t2 = cashLT1_t1 + S2 * q1_LT1 - feesPaidLT1_t2;

  // LT2 buys i units at S2
  const tradeLT2_t2 = i;
  const feesPaidLT2_t2 = includeFees ? eta * Math.abs(tradeLT2_t2) : 0;
  const cashLT2_t2 = W0 - S2 * i - feesPaidLT2_t2;

  const MM_t2: AgentState = {
    inventory: 0,
    cash: cashMM_t2,
    cumulativePnL: cashMM_t2 - W0,
    riskExposure: 0,
    tradeThisPeriod: tradeMM_t2,
  };

  const LT1_t2: AgentState = {
    inventory: 0,
    cash: cashLT1_t2,
    cumulativePnL: cashLT1_t2 - W0,
    riskExposure: 0,
    tradeThisPeriod: tradeLT1_t2,
  };

  const LT2_t2: AgentState = {
    inventory: i,
    cash: cashLT2_t2,
    cumulativePnL: cashLT2_t2 - W0,
    riskExposure: computeRiskExposure(i, sigma),
    tradeThisPeriod: tradeLT2_t2,
  };

  // ========== TIME 3 ==========
  // No trading at t=3, final values realized
  // LT2's inventory is now worth S3 per unit

  const MM_t3: AgentState = {
    inventory: 0,
    cash: cashMM_t2,
    cumulativePnL: cashMM_t2 - W0,
    riskExposure: 0,
    tradeThisPeriod: 0,
  };

  const LT1_t3: AgentState = {
    inventory: 0,
    cash: cashLT1_t2,
    cumulativePnL: cashLT1_t2 - W0,
    riskExposure: 0,
    tradeThisPeriod: 0,
  };

  // LT2 at t=3: asset value realized
  const LT2_t3: AgentState = {
    inventory: i, // Still holds but now valued at S3
    cash: cashLT2_t2,
    cumulativePnL: cashLT2_t2 + i * eq.S3 - W0,
    riskExposure: 0,
    tradeThisPeriod: 0,
  };

  return {
    MM: { 1: MM_t1, 2: MM_t2, 3: MM_t3 },
    LT1: { 1: LT1_t1, 2: LT1_t2, 3: LT1_t3 },
    LT2: { 1: LT2_t1, 2: LT2_t2, 3: LT2_t3 },
  };
}

/**
 * Generate shocks for the simulation
 */
function generateShocks(sigma: number, options?: SimulationOptions): Shocks {
  if (options?.deterministicPath) {
    return {
      epsilon2: options.fixedEpsilon2 ?? 0,
      epsilon3: options.fixedEpsilon3 ?? 0,
    };
  }

  const seed = options?.seed ?? generateRandomSeed();
  const rng = new SeededRNG(seed);

  return {
    epsilon2: rng.nextGaussian(0, sigma),
    epsilon3: rng.nextGaussian(0, sigma),
  };
}

/**
 * Main entry point - run full simulation
 */
export function runSimulation(
  params: ModelParams,
  options?: SimulationOptions
): SimulationState {
  // Validate parameters
  const validation = validateParams(params);
  if (!validation.valid) {
    throw new Error(`Invalid parameters: ${JSON.stringify(validation.errors)}`);
  }

  // Generate or use fixed shocks
  const shocks = generateShocks(params.sigma, options);

  // Compute equilibrium
  const equilibrium = computeEquilibrium(params, shocks);

  // Compute agent states
  const agents = computeAgentStates(params, equilibrium, shocks);

  return {
    params,
    shocks,
    equilibrium,
    agents,
    currentTime: 1,
    deterministicMode: options?.deterministicPath ?? false,
    seed: options?.seed,
  };
}

/**
 * Compute expected excess return for MMs (PRD FR-20)
 */
export function computeExpectedExcessReturn(params: ModelParams): {
  total: number;
  perUnit: number;
} {
  const { a, sigma, i, n } = params;
  const liquidityDiscount = (a * Math.pow(sigma, 2) * i) / (n + 1);

  return {
    total: liquidityDiscount,
    perUnit: a * Math.pow(sigma, 2),
  };
}
